import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import {
  AppState,
  AppTheme,
  ProfileReducerState,
  DiscipleshipClassReducerState,
  DiscipleshipClassInterface,
  UserInterface
} from "../../../../interfaces";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import {
  Button,
  Grid,
  Container,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment,
  Avatar
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { DialogModal, ErrorView } from "../../../../components";
import { clearDiscipleshipClassError, getUserDiscipleshipClass, deleteUserDiscipleshipClass } from "../../../../store/actions";
import { checkUserAccess } from "../../../../util/permission-checker";
import PopupMenuDialog from "../../../../components/PopupMenuDialog";
import AddUserDiscipleshipClassDetailsModal from "./AddUserDiscipleshipClassDetailsModal";
import userImg from "../../../../assets/user-img.png";

interface UserDiscipleshipClassesProps {
  discipleshipClass: DiscipleshipClassReducerState;
  discipleshipClassData: DiscipleshipClassInterface | null;
  profile: ProfileReducerState;
  appTheme: AppTheme;
  getUserDiscipleshipClass: (_id: string, search: string, page: number, per_page: number) => void;
  clearDiscipleshipClassError: () => void;
  deleteUserDiscipleshipClass: (_id: string, subtractor_id: string) => void;
}

interface UserDiscipleshipClassModalProps {
  open: boolean;
  discipleshipClassData: DiscipleshipClassInterface | null;
  onDismissed: () => void;
}

const useStyles = (appTheme: AppTheme) =>
  makeStyles((theme: Theme) =>
    createStyles({
      root: {
        flexGrow: 1,
        width: "100%"
      },
      avatar: {
        height: 80,
        width: 80,
        display: "block",
        margin: "auto",
        border: "1px solid black"
      },
      icon: {
        color: theme.palette.text.primary,
        padding: 10,
        borderRadius: 30,
        backgroundColor: appTheme.iconBackgroundColor,
        "&:hover": {
          backgroundColor: appTheme.iconBackgroundHoverColor
        }
      },
      searchButton: {
        marginRight: "-21px",
        height: "55px",
        borderRadius: "30px",
        width: "100px"
      },
      textInput: {
        marginTop: "5px",
        width: "300px"
      },
      textInputControl: {
        borderRadius: "30px",
        backgroundColor: appTheme.section.backgroundColor
      },
      container: {
        minHeight: "60vh"
      },
      textInputIcon: {
        color: "#000"
      }
    })
  );

const UserDiscipleshipClasses: React.FC<UserDiscipleshipClassesProps> = (props) => {
  const {
    appTheme,
    discipleshipClassData,
    discipleshipClass,
    profile,
    getUserDiscipleshipClass,
    deleteUserDiscipleshipClass,
    clearDiscipleshipClassError
  } = props;

  const [_id] = useState<string>(discipleshipClassData ? discipleshipClassData.discipleship_class_id : "");
  const [search, setSearch] = useState<string>(discipleshipClass.search);
  const [page, setPage] = useState<number>(discipleshipClass.page);
  const [perPage, setPerPage] = useState<number>(discipleshipClass.per_page ?? 20);
  const [selectedRecord, setSelectedRecord] = useState<UserInterface | null>(null);
  const [popUpDialogOpen, setPopUpDialogOpen] = useState<boolean>(false);

  const popUpMenuItems = useMemo(() => {
    return [{ text: "Delete Discipleship Class Student", permission: "delete-discipleship-class-student" }];
  }, []);

  const [menuItems, setMenuItems] = useState<Array<string>>(
    popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text)
  );

  useEffect(() => {
    setMenuItems(popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text));
  }, [popUpMenuItems, profile.permissions]);

  const [addUserDiscipleshipClassDetailsModalOpen, setAddUserDiscipleshipClassDetailsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    getUserDiscipleshipClass(_id, search, page, perPage);
  }, [_id, search, page, perPage, getUserDiscipleshipClass]);

  const styles = useStyles(appTheme)();

  type DialogOptions = {
    open: boolean;
    body: string;
  };

  const [dialogOptions, setDialogOptions] = useState<DialogOptions>({ open: false, body: "" });

  const dismissDialogModal = useCallback(() => {
    setDialogOptions({ open: false, body: "" });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      clearDiscipleshipClassError();
    };
  }, [clearDiscipleshipClassError]);

  useEffect(() => {
    if (!discipleshipClass.error && discipleshipClass.response && discipleshipClass.response.data.status !== "Ok") {
      clearDiscipleshipClassError();

      setSelectedRecord(null);

      setAddUserDiscipleshipClassDetailsModalOpen(false);

      setDialogOptions({ open: true, body: discipleshipClass.response.data.status });

      getUserDiscipleshipClass(_id, "", 0, perPage);
    }
  }, [discipleshipClass.error, discipleshipClass.response, _id, perPage, getUserDiscipleshipClass, clearDiscipleshipClassError]);

  useEffect(() => {
    if (addUserDiscipleshipClassDetailsModalOpen) {
      return;
    }

    const requestData = discipleshipClass.error ? discipleshipClass : null;

    if (!requestData) {
      return;
    }

    if (requestData.error && requestData.error.data && requestData.error.status) {
      let body: string = requestData.error.data.status;

      if (requestData.error.status === 500) {
        body = requestData.error.data.content;
      }

      setDialogOptions({ open: true, body: body });
    } else {
      if (requestData.error && requestData.error.message) {
        setDialogOptions({ open: true, body: requestData.error.message });
      }
    }
  }, [addUserDiscipleshipClassDetailsModalOpen, discipleshipClass]);

  const onMenuItemSelected = (menuItem: string) => {
    setPopUpDialogOpen(false);

    if (!selectedRecord) {
      return;
    }

    switch (menuItem) {
      case "Delete Discipleship Class Student":
        if (window.confirm("Would you like to delete this discipleship class student ?")) {
          deleteUserDiscipleshipClass(_id, selectedRecord.user_id);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.root}>
      <DialogModal open={dialogOptions.open} body={dialogOptions.body} onDismissed={dismissDialogModal} />
      <AddUserDiscipleshipClassDetailsModal
        open={addUserDiscipleshipClassDetailsModalOpen}
        onDismissed={() => {
          setSearch("");
          setPage(0);
          setAddUserDiscipleshipClassDetailsModalOpen(false);
        }}
        discipleshipClassData={discipleshipClassData}
      />
      <PopupMenuDialog
        open={popUpDialogOpen}
        onDismissed={() => setPopUpDialogOpen(false)}
        onMenuItemSelected={onMenuItemSelected}
        menuItems={menuItems}
      />
      <Container maxWidth="lg">
        {!addUserDiscipleshipClassDetailsModalOpen && discipleshipClass.error ? (
          <ErrorView error={discipleshipClass.error} onRetry={() => getUserDiscipleshipClass(_id, search, page, perPage)} />
        ) : (
          <React.Fragment>
            <Grid container justifyContent="space-between" spacing={0}>
              <Grid item>
                <Grid container spacing={1} alignItems="center">
                  <Grid item>{discipleshipClass.loading && <CircularProgress style={{ display: "block", margin: "auto" }} color="primary" />}</Grid>
                  <Grid item>
                    <Typography variant="h3" color="primary">
                      Discipleship Class Students
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <TextField
                  onChange={(discipleshipClass) => {
                    setSearch(discipleshipClass.currentTarget.value);
                  }}
                  error={false}
                  value={search}
                  variant="outlined"
                  placeholder="Search ..."
                  className={styles.textInput}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search className={styles.textInputIcon} />
                      </InputAdornment>
                    ),
                    classes: { root: styles.textInputControl }
                  }}
                />
              </Grid>
            </Grid>
            <br />
            <TableContainer className={styles.container}>
              <Table stickyHeader aria-label="discipleship-class-students">
                <TableHead>
                  <TableRow>
                    {[
                      { label: "Profile Image", minWidth: 50 },
                      { label: "ID", minWidth: 100 },
                      { label: "Name", minWidth: 150 },
                      { label: "Type", minWidth: 100 },
                      { label: "Username", minWidth: 80 },
                      { label: "Email", minWidth: 100 },
                      { label: "Mobile No", minWidth: 100 },
                      { label: "Residence", minWidth: 150 },
                      { label: "Options", minWidth: 100 }
                    ].map((column) => (
                      <TableCell key={column.label} align="center" style={{ minWidth: column.minWidth }}>
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {discipleshipClass.data.map((user: UserInterface) => {
                    const _id = user.user_id;
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={_id}>
                        {["img", "user_id", "name", "user_type", "username", "email", "mobile_no", "residence", "options"].map((column) => {
                          const key = `${_id}-${column}`;

                          const data = (user as unknown as { [key: string]: any })[column];
                          if (column === "options") {
                            return (
                              <TableCell key={key} align="center">
                                <Button
                                  variant="outlined"
                                  disabled={menuItems.length === 0}
                                  onClick={() => {
                                    setSelectedRecord(user);
                                    setPopUpDialogOpen(true);
                                  }}>
                                  Options
                                </Button>
                              </TableCell>
                            );
                          } else if (column === "img") {
                            return (
                              <TableCell key={key} align="center">
                                <Avatar className={styles.avatar} alt={user.name} src={data ? data : userImg} />
                              </TableCell>
                            );
                          } else {
                            return (
                              <TableCell key={key} align="center">
                                {data}
                              </TableCell>
                            );
                          }
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Grid container justifyContent="space-between" style={{ margin: "0px 10px 0px 10px" }}>
              <Grid item>
                <Button
                  disabled={!checkUserAccess("add-discipleship-class-student", profile.permissions)}
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setSelectedRecord(null);
                    setAddUserDiscipleshipClassDetailsModalOpen(true);
                  }}>
                  Add New Discipleship Class Student
                </Button>
              </Grid>
              <Grid item>
                <TablePagination
                  component="div"
                  count={discipleshipClass.total}
                  page={page}
                  onPageChange={(_, page) => setPage(page)}
                  rowsPerPage={perPage}
                  rowsPerPageOptions={[20, 50, 100, 1000]}
                  onRowsPerPageChange={(discipleshipClass) => {
                    setPerPage(parseInt(discipleshipClass.target.value));
                    setPage(0);
                  }}
                />
              </Grid>
            </Grid>
          </React.Fragment>
        )}
      </Container>
    </div>
  );
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, void, AnyAction>) => {
  return {
    getUserDiscipleshipClass: (_id: string, search: string, page: number, per_page: number) =>
      dispatch(getUserDiscipleshipClass(_id, search, page, per_page)),
    clearDiscipleshipClassError: () => dispatch(clearDiscipleshipClassError()),
    deleteUserDiscipleshipClass: (_id: string, subtractor_id: string) => dispatch(deleteUserDiscipleshipClass(_id, subtractor_id))
  };
};

const mapStateToProps = (state: AppState) => {
  return {
    discipleshipClass: state.discipleshipClass,
    profile: state.profile,
    appTheme: state.general.theme
  };
};

const UserDiscipleshipClassesComponent = connect(mapStateToProps, mapDispatchToProps)(UserDiscipleshipClasses);

const UserDiscipleshipClassModal: React.FC<UserDiscipleshipClassModalProps> = (props) => {
  return (
    <DialogModal
      maxWidth="lg"
      title="Discipleship Class Students"
      component={<UserDiscipleshipClassesComponent discipleshipClassData={props.discipleshipClassData} />}
      contentType="custom"
      open={props.open}
      onDismissed={props.onDismissed}
    />
  );
};

export default UserDiscipleshipClassModal;
